<template>
  <div
    class="xform-section"
    id="address"
    :class="[{ draw: entityTypeId === ConsentServiceEntityTypeIds.Draw }]"
  >
    <h3 v-if="showHeader">{{ header }}</h3>
    <div class="xfields">
      <base-form-field
        :schema="addressSchema.address1"
        v-model="address.address1"
        @update:model-value="handleFieldChange('address1', $event)"
      />
      <div
        v-if="showUnitType"
        class="xfield form-field"
        :disabled="disabled"
        id="unitType"
      >
        <div class="input-wrapper-outer">
          <label class="label cuscd-form-field-label" for="addressUnitTypeId"
            >Unit Type</label
          >
          <VeeField
            name="addressUnitTypeId"
            v-model="address.addressUnitTypeId"
            v-slot="{ field }"
            @handleChange="handleFieldChange('addressUnitTypeId', $event)"
          >
            <select
              class="form-control select"
              v-model="address.addressUnitTypeId"
              v-bind="field"
              id="addressUnitTypeId"
            >
              <option :value="null"></option>
              <option
                v-for="option in addressUnitTypes"
                :key="option.id"
                :title="option.name"
                :selected="option.id === Number(address.addressUnitTypeId)"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
          </VeeField>
          <div>
            <ErrorMessage class="error-message" name="addressUnitTypeId" />
          </div>
        </div>
      </div>

      <div
        class="xfield form-field xfield-group"
        :disabled="disabled"
        id="unitNumber"
        v-if="showUnitType"
      >
        <div class="input-wrapper-outer">
          <label class="label cuscd-form-field-label" for="address2"
            >Unit Number</label
          >
          <div class="input-wrapper-inner">
            <span class="prefix">{{ selectedUnitType?.abbreviation }}</span>
            <VeeField
              type="text"
              id="address2"
              name="address2"
              v-model="address.address2"
              @handleChange="handleFieldChange('address2', $event)"
            />
          </div>
          <div>
            <ErrorMessage class="error-message" name="address2" />
          </div>
        </div>
      </div>

      <base-form-field
        v-if="!showUnitType"
        :schema="addressSchema.address2"
        v-model="address.address2"
        @update:model-value="handleFieldChange('address2', $event)"
      />
      <base-form-field
        :schema="addressSchema.city"
        v-model="address.city"
        @update:model-value="handleFieldChange('city', $event)"
      />
      <base-form-field
        :schema="addressSchema.stateId"
        v-model.number="address.stateId"
        :options="statesOptions"
        @update:model-value="handleFieldChange('stateId', $event)"
      />
      <base-form-field
        :schema="addressSchema.postalCode"
        v-model="address.postalCode"
        @update:model-value="handleFieldChange('postalCode', $event)"
      />
      <base-form-field
        :schema="addressSchema.countryId"
        v-model.number="address.countryId"
        :options="countryOptions"
        @update:model-value="handleFieldChange('countryId', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch, ref, toRefs } from "vue";
import BaseFormField from "@/components/form-fields/BaseFormField.vue";
import { useTypeStore } from "@/store/store/typeStore";
import { AddressTypeIds } from "@/types/address";
import { Field as VeeField, ErrorMessage } from "vee-validate";
import { useAddressForm } from "@/composables/address/useAddressForm";
import { FormModes } from "@/types/form";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import type { ApplicantAddress } from "@/types/applicant";
import { LOSType } from "@/models/opal";

useTypeStore();

const emit = defineEmits<{
  (e: "update:address", address: ApplicantAddress): void;
}>();

const props = withDefaults(
  defineProps<{
    disabled: boolean;
    mode: keyof typeof FormModes;
    initialValues?: any;
    formData?: Record<string, any>;
    header: string;
    entityTypeId?: number;
    showHeader?: boolean;
    showUnitType?: boolean;
  }>(),
  {
    disabled: true,
    active: false,
    header: "Address",
    entityTypeId: ConsentServiceEntityTypeIds.Application,
    showHeader: true,
    showUnitType: true,
  }
);

const { mode, entityTypeId, initialValues, showUnitType } = toRefs(props);

const address = ref<ApplicantAddress>({
  address1: "",
  address2: null as null | string,
  addressId: props.initialValues?.addressId || 0,
  addressTypeId: props.initialValues?.addressTypeId || AddressTypeIds.PERMANENT,
  addressTypeName: props.initialValues?.addressTypeName || "",
  addressUnitTypeId: null as null | number,
  addressUnitTypeName: null as null | string,
  applicantId: props.initialValues?.applicantId || 0,
  id: props.initialValues?.id || 0,
  city: "",
  stateId: 0,
  stateName: "",
  postalCode: "",
  countryId: 0,
  countryName: "",
});

const {
  address2,
  addressSchema,
  addressBackup,
	addressUnitTypePrefixes,
  addressUnitTypes,
  addressValidationSchema,
  countryOptions,
  discardChanges,
  errors,
  formattedAddress2,
  hasChanges,
  handleFieldChange,
  hasErrors,
  originalAddress,
  postalCode,
  postalCodeExtension,
  prefixes,
  removePrefix,
  resetSchemas,
  selectedUnitType,
  setAddress,
  setValues,
  statesOptions,
  updatedAddress,
  values,
} = useAddressForm({
  address,
  mode,
  entityTypeId,
  initialValues,
  showUnitType,
});

function setAddressInputs(
  values: undefined | null | Record<string, any>,
  shouldValidate?: undefined | boolean
) {
  if (values) {
    const currentValues = JSON.parse(JSON.stringify(values));

    const selectedUnitType =
      currentValues?.addressUnitTypeId && currentValues?.addressUnitTypeId > 0
        ? addressUnitTypes.value.find(
            (t: LOSType) => t.id === Number(currentValues?.addressUnitTypeId)
          )
        : null;

    address.value.address1 = currentValues.address1;
    address.value.address2 = showUnitType.value
      ? currentValues.address2
      : removePrefix(currentValues.address2, prefixes.value);
    address.value.addressId = currentValues?.addressId;
    address.value.addressTypeId =
      currentValues?.addressTypeId || AddressTypeIds.PERMANENT;
    address.value.addressTypeName = currentValues?.addressTypeName;
    address.value.addressUnitTypeId = currentValues?.addressUnitTypeId;
    address.value.addressUnitTypeName = selectedUnitType?.name || null;
    address.value.applicantId = currentValues?.applicantId;
    address.value.id = currentValues?.id;
    address.value.city = currentValues?.city;
    address.value.stateId = currentValues?.stateId;
    address.value.stateName = currentValues?.stateName;
    address.value.postalCode = currentValues?.postalCode;
    address.value.countryId = currentValues?.countryId;
    address.value.countryName = currentValues?.countryName;
    // setValues(accountDetails.value, shouldValidate);
  }
}

onMounted(() => {
  setAddress();
});

watch(
  () => address.value,
  () => {
    setValues(address.value);
    emit("update:address", address.value);
  },
  { deep: true }
);

watch(
  () => props.initialValues,
  () => {
    setAddress();
  },
  { deep: true }
);

watch(
  () => props.disabled,
  () => {
    if (props.disabled) {
      resetSchemas();
    }
  }
);

defineExpose({
  address,
  address2,
  addressBackup,
  addressSchema,
	addressUnitTypePrefixes,
  addressUnitTypes,
  addressValidationSchema,
  countryOptions,
  discardChanges,
  errors,
  formattedAddress2,
  hasChanges,
  hasErrors,
  originalAddress,
  postalCode,
  postalCodeExtension,
  resetSchemas,
  selectedUnitType,
  setAddress,
	setAddressInputs,
  setValues,
  statesOptions,
  updatedAddress,
  values,
});
</script>

<style scoped>
.xform-section div.xfields div.xfield {
  width: calc(100% / 3 - 20px);
}

.xfield-100 {
  width: 100%;
}
.xform-section div.xfields div.xfield .form-control.valid {
  border: 2px solid #dddddd;
  background-image: none;
}

.xform-section.draw {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.xform-section.draw div.xfields div.xfield {
  margin-bottom: 0;
}

@media screen and (max-width: 800px) {
  .xform-section div.xfields {
    display: block !important;
  }
  .xform-section div.xfields div.xfield {
    width: calc(100% - 20px);
  }
}
</style>
<style scoped src="@/assets/css/address2.css"></style>
